import { useState } from 'react'
import { File, ListFilter, User } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  IntuiDropdownMenu,
  IntuiDropdownMenuContent,
  IntuiDropdownMenuItem,
  IntuiDropdownMenuSub,
  IntuiDropdownMenuSubContent,
  IntuiDropdownMenuSubTrigger,
  IntuiDropdownMenuTrigger,
} from '@/components/intueri/IntuiDropdownMenu'
import { NODE_TYPES } from './nodeIcons'
import type { MockNode } from './types'
import { countActive, presentUploaders, toggleValue, type KgFacet, type KgFilters } from './filters'
import { useFacetLabel } from './useFacetLabel'

interface Props {
  nodes: MockNode[]
  filters: KgFilters
  onChange: (next: KgFilters) => void
}

/**
 * The Filter button and its cascading menu, as the Figma draws it: a
 * "Filter" heading, then Entity Type (file icon) and Uploaded by (user icon),
 * each a list that stays open while values are picked and shows the picked
 * ones highlighted. The active count sits on the button; the chips under
 * the toolbar say which, and remove one at a time.
 */
const FilterPopover = ({ nodes, filters, onChange }: Props) => {
  const { t } = useTranslation()
  const labelFor = useFacetLabel()
  const active = countActive(filters)
  const uploaders = presentUploaders(nodes)
  // The design's Uploaded by list opens with a search field above the names,
  // so a long uploader list is reached by typing. The query lives for one
  // opening of the menu.
  const [uploaderQuery, setUploaderQuery] = useState('')
  const uploaderSearchLabel = t('knowledge_graph:filter.search_uploaders', { defaultValue: 'Search uploaders...' })

  // The design's menu rows at their true size (the Figma export is 2x):
  // 14px medium text on a 32px row. The root menu keeps 4px around its rows
  // (8px inside them); the submenu 6px around its field and names (6px
  // inside the names).
  const rowClass = 'h-8 text-sm font-medium'

  const submenu = (facet: KgFacet, title: string, Icon: typeof File, values: string[], disabled = false) => (
    <IntuiDropdownMenuSub>
      <IntuiDropdownMenuSubTrigger disabled={disabled} className={`${rowClass} px-2`}>
        <Icon className="size-3.5 mr-2.5 text-foreground" />
        {title}
      </IntuiDropdownMenuSubTrigger>
      <IntuiDropdownMenuSubContent className="z-[1000000] w-[198px] p-1.5" aria-label={title}>
        {facet === 'uploaders' && (
          /* `stopPropagation` because Radix menus own the keyboard for
             typeahead — without it every keystroke jumps the highlighted
             item instead of reaching the field. Same look as the toolbar's
             search field: white, neutral-200 border, 14px placeholder. */
          <div className="mb-1.5" onKeyDown={(e) => e.stopPropagation()}>
            <Input
              value={uploaderQuery}
              onChange={(e) => setUploaderQuery(e.target.value)}
              placeholder={String(uploaderSearchLabel)}
              aria-label={String(uploaderSearchLabel)}
              className="h-8 rounded-md border-neutral-200 bg-background px-2.5 text-sm shadow-none placeholder:text-muted-foreground md:text-sm dark:border-neutral-700"
            />
          </div>
        )}
        {values.map((value) => {
          const selected = (filters[facet] as string[]).includes(value)
          return (
            <IntuiDropdownMenuItem
              key={value}
              role="menuitemcheckbox"
              aria-checked={selected}
              className={`${rowClass} px-1.5${selected ? ' bg-accent' : ''}`}
              // Keep the menu open: picking several values is the common case.
              onSelect={(event) => {
                event.preventDefault()
                onChange(toggleValue(filters, facet, value as never))
              }}
            >
              {labelFor(facet, value)}
            </IntuiDropdownMenuItem>
          )
        })}
        {facet === 'uploaders' && values.length === 0 && uploaders.length > 0 && (
          <p className="px-1.5 py-1.5 text-sm text-muted-foreground">
            {t('knowledge_graph:filter.no_uploaders_match', { defaultValue: 'No uploaders match.' })}
          </p>
        )}
      </IntuiDropdownMenuSubContent>
    </IntuiDropdownMenuSub>
  )

  const query = uploaderQuery.trim().toLowerCase()
  const shownUploaders = query ? uploaders.filter((u) => labelFor('uploaders', u).toLowerCase().includes(query)) : uploaders

  return (
    <IntuiDropdownMenu onOpenChange={(open) => !open && setUploaderQuery('')}>
      <IntuiDropdownMenuTrigger asChild>
        <Button
          variant="outline"
          // Matches the search field beside it: 36px, neutral-200 border, 14px text.
          className="h-9 gap-2 rounded-md border-neutral-200 bg-background px-4 text-sm font-medium shadow-none dark:border-neutral-700"
          data-active={active > 0}
        >
          <ListFilter className="size-3.5" />
          {t('knowledge_graph:toolbar.filter', { defaultValue: 'Filter' })}
          {active > 0 && (
            <span
              className="grid min-w-5 place-items-center rounded-full bg-foreground px-1.5 text-[11px] font-semibold leading-5 text-background"
              data-testid="kg-filter-count"
            >
              {active}
            </span>
          )}
        </Button>
      </IntuiDropdownMenuTrigger>
      {/* Opens on the toolbar's bottom border, as the design draws it. */}
      <IntuiDropdownMenuContent align="start" sideOffset={13} className="z-[1000000] w-[198px] p-1">
        <div className="px-2 py-1 text-[13px] font-medium text-muted-foreground">
          {t('data_catalog:filter.title', { defaultValue: 'Filter' })}
        </div>
        {submenu('types', t('knowledge_graph:filter.entity_type', { defaultValue: 'Entity Type' }), File, NODE_TYPES)}
        {submenu(
          'uploaders',
          t('knowledge_graph:filter.uploaded_by', { defaultValue: 'Uploaded by' }),
          User,
          shownUploaders,
          uploaders.length === 0,
        )}
      </IntuiDropdownMenuContent>
    </IntuiDropdownMenu>
  )
}

export default FilterPopover
